import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Search, Loader2, Copy, Check, Download, Printer, Sparkles, FolderOpen,
} from 'lucide-react';
import { Sidebar, Topbar } from './Dashboard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { auth, templates, clients, getStoredUser } from '../api';
import { FullscreenLoading } from './Time';

const CATEGORY_COLORS = {
  'SARS': 'bg-emerald-50 text-emerald-800 border-emerald-100',
  'CIPC': 'bg-purple-50 text-purple-800 border-purple-100',
  'Beneficial Ownership': 'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-100',
  'Corporate': 'bg-blue-50 text-blue-800 border-blue-100',
  'Engagement': 'bg-amber-50 text-amber-800 border-amber-100',
  'General': 'bg-stone-100 text-stone-700 border-stone-200',
};

export default function Templates() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [templateList, setTemplateList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [rendered, setRendered] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    (async () => {
      try {
        const [me, tl, cl] = await Promise.all([
          auth.me().catch(() => null),
          templates.list(),
          clients.list(),
        ]);
        if (me) setUser(me);
        setTemplateList(tl);
        setClientList(cl);
        if (tl.length > 0) setSelectedId(tl[0].id);
        if (cl.length > 0) setSelectedClientId(cl[0].id);
      } finally { setLoading(false); }
    })();
  }, [nav]);

  const categoriesInList = useMemo(() => {
    const set = new Set(templateList.map((t) => t.category));
    return ['All', ...Array.from(set)];
  }, [templateList]);

  const filtered = useMemo(() => templateList.filter((t) => {
    if (category !== 'All' && t.category !== category) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
  }), [templateList, category, query]);

  const selected = templateList.find((t) => t.id === selectedId);

  const doRender = async () => {
    if (!selectedId || !selectedClientId) return;
    setRendering(true);
    setRendered(null);
    try {
      const r = await templates.render(selectedId, selectedClientId);
      setRendered(r);
    } catch (err) {
      toast({ title: 'Render failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setRendering(false); }
  };

  const copy = async () => {
    if (!rendered) return;
    try {
      await navigator.clipboard.writeText(rendered.content);
      setCopied(true);
      toast({ title: 'Copied to clipboard' });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: 'Copy failed', description: 'Please copy the text manually.', variant: 'destructive' });
    }
  };

  const download = () => {
    if (!rendered) return;
    const blob = new Blob([rendered.content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const clientSlug = (rendered.client?.name || 'client').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    a.download = `${rendered.id}-${clientSlug}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printDoc = () => {
    if (!rendered) return;
    const w = window.open('', '_blank', 'width=800,height=900');
    if (!w) return;
    const escaped = rendered.content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    w.document.write(`<!doctype html><html><head><title>${rendered.title}</title>` +
      '<style>body{font-family:Georgia,serif;white-space:pre-wrap;padding:2rem;max-width:800px;line-height:1.5;}</style>' +
      `</head><body>${escaped}</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 200);
  };

  if (loading) return <FullscreenLoading label="Loading templates..."/>;

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Templates"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900 flex items-center gap-2">
              <FileText size={24} className="text-emerald-800"/> Templates
            </h1>
            <p className="mt-1 text-stone-600 text-sm">Pick a template, pick a client, and the details merge automatically. Copy, download or print.</p>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            {/* LEFT: template library */}
            <div className="lg:col-span-4 rounded-xl bg-white border border-stone-200 overflow-hidden flex flex-col">
              <div className="p-4 border-b border-stone-100 space-y-3">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
                  <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search templates" className="pl-9 h-9"/>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {categoriesInList.map((c) => (
                    <button key={c} type="button" onClick={() => setCategory(c)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${category === c ? 'bg-emerald-800 text-white border-emerald-800' : 'bg-white text-stone-700 border-stone-200 hover:border-emerald-500'}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="divide-y divide-stone-100 overflow-y-auto max-h-[70vh]">
                {filtered.length === 0 && (
                  <div className="p-6 text-center text-sm text-stone-500">No templates match.</div>
                )}
                {filtered.map((t) => {
                  const cat = CATEGORY_COLORS[t.category] || CATEGORY_COLORS['General'];
                  const active = selectedId === t.id;
                  return (
                    <button key={t.id} onClick={() => { setSelectedId(t.id); setRendered(null); }}
                      className={`w-full text-left p-4 transition-colors ${active ? 'bg-emerald-50' : 'hover:bg-stone-50'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium text-stone-900 text-sm leading-tight">{t.title}</div>
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${cat} flex-shrink-0`}>{t.category}</span>
                      </div>
                      <div className="mt-1 text-xs text-stone-500 leading-snug">{t.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* RIGHT: preview + client picker */}
            <div className="lg:col-span-8 space-y-4">
              <div className="rounded-xl bg-white border border-stone-200 p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <div className="text-xs text-stone-500 font-medium">Selected template</div>
                    <div className="font-semibold text-stone-900">{selected?.title || 'Choose a template on the left'}</div>
                    {selected && <div className="text-xs text-stone-500 mt-0.5">{selected.description}</div>}
                  </div>
                  <div className="sm:w-64">
                    <div className="text-xs text-stone-500 font-medium mb-1">Merge details from</div>
                    <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                      <SelectTrigger className="h-10"><SelectValue placeholder="Select a client"/></SelectTrigger>
                      <SelectContent>
                        {clientList.length === 0 && <div className="px-3 py-2 text-xs text-stone-500">No clients yet</div>}
                        {clientList.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={doRender} disabled={rendering || !selectedId || !selectedClientId} className="bg-emerald-800 hover:bg-emerald-900 text-white h-10 self-end">
                    {rendering ? <><Loader2 size={16} className="mr-2 animate-spin"/> Merging...</> : <><Sparkles size={14} className="mr-2"/> Generate</>}
                  </Button>
                </div>
              </div>

              <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-stone-100">
                  <div className="flex items-center gap-2">
                    <FolderOpen size={16} className="text-emerald-800"/>
                    <div className="font-semibold text-stone-900">Preview</div>
                    {rendered && <Badge variant="outline" className="border-stone-300 text-xs">for {rendered.client?.name}</Badge>}
                  </div>
                  {rendered && (
                    <div className="flex items-center gap-2">
                      <Button onClick={copy} size="sm" variant="outline" className="h-8">
                        {copied ? <><Check size={13} className="mr-1.5"/> Copied</> : <><Copy size={13} className="mr-1.5"/> Copy</>}
                      </Button>
                      <Button onClick={download} size="sm" variant="outline" className="h-8"><Download size={13} className="mr-1.5"/> Download</Button>
                      <Button onClick={printDoc} size="sm" className="h-8 bg-emerald-800 hover:bg-emerald-900 text-white"><Printer size={13} className="mr-1.5"/> Print</Button>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  {!rendered && !rendering && (
                    <div className="py-14 text-center text-stone-500">
                      <FileText size={32} className="mx-auto text-stone-300"/>
                      <div className="mt-3 text-sm">Choose a template, pick a client and click <strong>Generate</strong> to see the merged letter here.</div>
                    </div>
                  )}
                  {rendering && (
                    <div className="py-14 text-center text-stone-500">
                      <Loader2 size={22} className="mx-auto animate-spin"/>
                      <div className="mt-2 text-sm">Merging client details...</div>
                    </div>
                  )}
                  {rendered && (
                    <pre className="whitespace-pre-wrap font-serif text-[15px] text-stone-800 leading-relaxed bg-stone-50 rounded-lg border border-stone-200 p-6 max-h-[68vh] overflow-y-auto">{rendered.content}</pre>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
